import Panel from '@/components/ui/Panel';
import { useCallback, useMemo, useState } from 'react';
import Button, { ButtonOrAnchorProps } from '../../Buttons/Button';
import Text from '@/components/cms/Text';
import { CreditPlanDebug, CreditPlanFailureReason } from '@/@types/client-api';
import DebugInformation from '../DebugInformation';
import { templateString } from '@/utils/helpers';
import { useErrorBoundary } from 'react-error-boundary';
import { useLazyDeleteCreditPlanQuery } from '@/redux/api/client/creditPlan';
import Shimmer from '../../Shimmer';
import useToast from '@/hooks/useToast';
import DebugButtons from '../DebugButtons';
import { IContent } from '@/@types/cms';
import { ComponentFailurePlaceholderBlock } from '@/@types/content';

export interface CreditPlanFailureSidePanelContentProps {
  creditPlanIncompleteFailureContent?: IContent[];
  creditPlanLoadFailureContent?: IContent[];
  jurisdictionName?: string;
  debugInformation?: CreditPlanDebug;
  debugModeEnabled?: boolean;
  failureReason?: CreditPlanFailureReason;
  openJurisdictionBrowseUrl?: string;
  renderWithPanelContent?: boolean;
  planId?: string;
  isLoading?: boolean;
  togglePanel?: (open: boolean) => void;
  handleBackButton?: () => void;
  createCreditPlan?: () => void;
  getCreditPlan?: () => void;
}

const CreditPlanFailureSidePanelContent: React.FC<CreditPlanFailureSidePanelContentProps> = ({
  creditPlanIncompleteFailureContent,
  creditPlanLoadFailureContent,
  jurisdictionName,
  debugInformation,
  debugModeEnabled,
  failureReason,
  openJurisdictionBrowseUrl,
  renderWithPanelContent,
  planId,
  isLoading,
  togglePanel,
  handleBackButton,
  createCreditPlan,
  getCreditPlan
}) => {
  const { showFailMessage } = useToast();
  const { resetBoundary } = useErrorBoundary();
  const [deletePlan] = useLazyDeleteCreditPlanQuery();
  const [isDeleting, setIsDeleting] = useState<boolean>();

  const handleRetry = useCallback(() => {
    resetBoundary();

    if (failureReason === CreditPlanFailureReason.UNABLE_TO_RETRIEVE_PLAN) {
      getCreditPlan?.();
    } else if (failureReason === CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION) {
      createCreditPlan?.();
    }
  }, [createCreditPlan, failureReason, getCreditPlan, resetBoundary]);

  const content = useMemo(() => {
    const isIncompleteFailure =
      failureReason === CreditPlanFailureReason.NO_CREDIT_REQUIREMENTS ||
      failureReason === CreditPlanFailureReason.NO_PROGRAMS_FOUND ||
      failureReason === CreditPlanFailureReason.NO_PROGRAMS_PICKED_FOR_PLAN;

    if (isIncompleteFailure) {
      const _content = creditPlanIncompleteFailureContent?.[0] as ComponentFailurePlaceholderBlock;

      return {
        heading:
          _content?.headingText ?? 'We’re sorry, we weren’t able to generate a full credit plan',
        body: (
          <span className="text-2">
            {templateString(
              _content?.bodyText ??
                'There was an issue in generating a credit plan.<br/><br/>In the meantime, please {Link}.',
              {
                Link: `<a href='${openJurisdictionBrowseUrl}'>browse our available programs for ${jurisdictionName}</a>`
              },
              true
            )}
          </span>
        ),
        buttonProps: {
          label: _content?.buttonText ?? 'Browse programs',
          iconRight: 'chevron-right',
          href: openJurisdictionBrowseUrl
        } as ButtonOrAnchorProps
      };
    }

    const _content = creditPlanLoadFailureContent?.[0] as ComponentFailurePlaceholderBlock;

    return {
      heading: _content?.headingText ?? 'Your credit plan failed to load',
      body:
        _content?.bodyText ??
        'Please reload the page. If the issue persists, please contact customer service.',
      buttonProps: {
        label: _content?.buttonText ?? 'Try again',
        loading: !!isLoading,
        onClick: handleRetry
      } as ButtonOrAnchorProps
    };
  }, [
    creditPlanIncompleteFailureContent,
    creditPlanLoadFailureContent,
    failureReason,
    handleRetry,
    isLoading,
    jurisdictionName,
    openJurisdictionBrowseUrl
  ]);

  const deleteCreditPlan = useCallback(async () => {
    if (planId) {
      setIsDeleting(true);
      const { isError, data } = await deletePlan(planId);
      setIsDeleting(false);

      if (!isError && !!data) {
        if (handleBackButton) handleBackButton();
        else if (togglePanel) togglePanel(false);
      } else {
        showFailMessage('Failed to delete plan');
      }
    }
  }, [planId, deletePlan, handleBackButton, togglePanel, showFailMessage]);

  const contents = useMemo(() => {
    return (
      <>
        <Panel.Body
          className="[&_[data-radix-scroll-area-viewport]>div]:h-full [&_a]:underline hover:[&_a]:text-red-brand [&_a_br]:hidden"
          scrollClassName="flex h-full flex-col justify-center"
        >
          {isDeleting || !!isLoading ? (
            <>
              <Shimmer className="mb-6 h-40 w-full md:h-20" />
              <Shimmer className="h-16 w-3/4 md:h-12" />
            </>
          ) : (
            <div className="flex flex-col items-start gap-6">
              {!!content.heading && (
                <Text element="h2" className="heading-2">
                  {content.heading}
                </Text>
              )}
              {typeof content.body === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: content.body }}></div>
              ) : (
                content.body
              )}
              {!!debugModeEnabled && (
                <div className="max-h-[800px] w-full">
                  <DebugInformation {...debugInformation} planId={planId ?? ''} />
                </div>
              )}
            </div>
          )}
        </Panel.Body>
        <Panel.Footer divider="line" className="flex items-center justify-end">
          <DebugButtons
            allowed={!!debugInformation?.allowed}
            isActive={!!debugModeEnabled}
            planId={planId}
            onDeletePress={deleteCreditPlan}
            isDeleting={!!isDeleting}
          />
          <Button type="button" color="outline-gray" size="large" {...content.buttonProps} />
        </Panel.Footer>
      </>
    );
  }, [
    content.body,
    content.buttonProps,
    content.heading,
    debugInformation,
    debugModeEnabled,
    deleteCreditPlan,
    isDeleting,
    isLoading,
    planId
  ]);

  return renderWithPanelContent ? (
    <Panel.Content onBackButtonPress={handleBackButton}>{contents}</Panel.Content>
  ) : (
    contents
  );
};

export default CreditPlanFailureSidePanelContent;
